import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { 
  HiOutlineUser, HiOutlinePhone, HiOutlineMail, 
  HiOutlineMapPin, HiOutlineCalendar, HiOutlineArrowLeft,
  HiOutlineCreditCard, HiOutlineWallet
} from "react-icons/hi2";
import { BsMailbox } from "react-icons/bs";

const PassengerDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract data received from FlightDetails route state
  const { flight, searchData, selectedAddons, finalPayable, resultToken } = location.state || {};

  const THEME_COLOR = "#166070";
  const userId = localStorage.getItem("guestUserId");

  // Dynamic passenger parsing configurations
  const adultCount = searchData?.Adults || 1;
  const childCount = searchData?.Children || 0;
  const infantCount = searchData?.Infants || 0;

  // --- COMPONENT LOCAL STATES ---
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [contact, setContact] = useState({
    email: "",
    phone: "",
    city: "",
    pincode: "",
    address: ""
  });
  
  const [passengers, setPassengers] = useState([]);

  // Fetch Wallet Balance dynamically on initialization
  useEffect(() => {
    if (userId) {
      fetchWalletBalance();
    }
  }, [userId]);

  const fetchWalletBalance = async () => {
    try {
      const res = await axios.get(`${baseurl}getWalletBalance/${userId}`);
      setWalletBalance(parseFloat(res.data.balance) || 0);
    } catch (err) {
      console.error("Error fetching wallet balance", err);
    }
  };

  // Setup dynamic passenger profiles with placeholders
  useEffect(() => {
    if (!location.state) return;

    const initialPassengers = [];
    for (let i = 0; i < adultCount; i++) {
      initialPassengers.push({ type: "Adult", title: "Mr", firstName: "", lastName: "", gender: "Male", dob: "" });
    }
    for (let i = 0; i < childCount; i++) {
      initialPassengers.push({ type: "Child", title: "Master", firstName: "", lastName: "", gender: "Male", dob: "" });
    }
    for (let i = 0; i < infantCount; i++) {
      initialPassengers.push({ type: "Infant", title: "Master", firstName: "", lastName: "", gender: "Male", dob: "" });
    }
    setPassengers(initialPassengers);
  }, [adultCount, childCount, infantCount, location.state]);

  // Fallback routing protection guardrail
  if (!location.state) {
    return (
      <div className="container py-5 text-center">
        <h4>No booking session found.</h4>
        <button className="btn mt-3 text-white" style={{ background: THEME_COLOR }} onClick={() => navigate("/")}>
          Go to Home
        </button>
      </div>
    );
  }

  // --- DYNAMIC FINANCIAL CALCULATIONS ---
  const GATEWAY_CHARGE_PERCENT = 0.027; // 2.7%
  const totalFareAmount = finalPayable || 0;

  const appliedWalletAmount = useWallet ? Math.min(walletBalance, totalFareAmount) : 0;
  const baseGatewayAmount = totalFareAmount - appliedWalletAmount;
  const platformFee = baseGatewayAmount > 0 ? Math.round(baseGatewayAmount * GATEWAY_CHARGE_PERCENT) : 0;
  const finalGatewayAmount = Math.round(baseGatewayAmount + platformFee);
  const netPayable = baseGatewayAmount === 0 ? totalFareAmount : finalGatewayAmount;

  // Update inline form changes dynamically
  const handlePassengerChange = (index, field, value) => {
    const updated = [...passengers];
    updated[index][field] = value;
    setPassengers(updated);
  };

  const getTodayDateString = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // --- RAZORPAY INJECTION HOOK ---
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // --- ORDER SUBMISSION AND PROCESSING CORE ---
  const handleCheckoutProcess = async (e) => {
    e.preventDefault();

    const isPassengersValid = passengers.every(p => p.firstName.trim() && p.lastName.trim() && p.dob);
    const isContactValid = contact.email && contact.phone && contact.city.trim() && contact.pincode.trim() && contact.address.trim();

    if (!isPassengersValid || !isContactValid) {
      alert("Please complete all fields, including Date of Birth and Contact information.");
      return;
    }

    setIsProcessing(true);

    // Load Razorpay dynamically for standard checkout channels
    if (baseGatewayAmount > 0) {
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        alert("Razorpay payment gateway failed to load. Please verify your connection.");
        setIsProcessing(false);
        return;
      }
    }

    const payloadContext = {
      flight,
      searchData,
      selectedAddons,
      passengers,
      contact,
      resultToken,
      userId,
      walletUsed: appliedWalletAmount,
      totalFare: totalFareAmount
    };

    try {
      // CASE 1: Full wallet balance payout execution
      if (useWallet && baseGatewayAmount === 0) {
        const res = await axios.post(`${baseurl}payFlightViaWalletOnly`, {
          ...payloadContext,
          amount: totalFareAmount
        });

        if (res.data.status === 'success') {
          // Captures returned primary tracking key context safely (id, record identification, or block reference)
          const bookingId = res.data.bookingDetails?.id || res.data.bookingDetails?.block_key || resultToken;
          navigate(`/flightinvoice`, { state: { id: bookingId } });
        } else {
          alert("Wallet processing transaction encountered a discrepancy error.");
        }
        setIsProcessing(false);
        return;
      }

      // CASE 2: External Gateway or Split Payment Order initialization
      const orderRes = await axios.post(`${baseurl}createFlightOrder`, {
        ...payloadContext,
        amount: finalGatewayAmount,
        platformFee: platformFee
      });

      console.log("orderRes", orderRes);

      const options = {
        key: "rzp_test_A8i5qZiJOeIYx8", 
        amount: orderRes.data.amount,
        currency: "INR",
        name: "Flight Booking Checkout",
        description: appliedWalletAmount > 0 ? "Split Pay: Wallet + Gateway Secure Checkout" : "Secure Payment Gateway Checkout",
        order_id: orderRes.data.id,
        handler: async function (response) {
          try {
            const verificationRes = await axios.post(`${baseurl}verifyFlightPayment`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              ...payloadContext,
              amountPaid: finalGatewayAmount
            });

            if (verificationRes.data.status === 'success') {
              // Extract unique tracking keys dynamically from ledger update block packages
              const bookingId = verificationRes.data.bookingDetails?.id || verificationRes.data.bookingDetails?.block_key || resultToken;
              navigate(`/flightinvoice`, { state: { id: bookingId } });
            } else {
              alert("Payment verification returned a mismatched check summary.");
              setIsProcessing(false);
            }
          } catch (verificationError) {
            console.error("Verification Endpoint Error Exception Context:", verificationError);
            alert("Error confirming reservation payment.");
            setIsProcessing(false);
          }
        },
        prefill: { email: contact.email, contact: contact.phone },
        theme: { color: THEME_COLOR },
        modal: {
          ondismiss: () => setIsProcessing(false)
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error("Transactional Pipeline Network Error Event:", err);
      alert(err.response?.data?.message || "Order orchestration pipeline encountered an issue. Please try again.");
      setIsProcessing(false);
    }
  };

  return (
    <div className="container py-lg-5 py-3 animate-fade-in">
      <button 
        type="button"
        className="btn border-0 p-0 mb-4 d-flex align-items-center gap-2 text-muted fw-semibold"
        onClick={() => navigate(-1)}
        disabled={isProcessing}
      >
        <HiOutlineArrowLeft /> Back to Flight Details
      </button>

      <form onSubmit={handleCheckoutProcess}>
        <div className="row g-4">
          
          {/* LEFT SIDEBAR PANEL: FORMS INGESTION FLOW */}
          <div className="col-lg-8">
            
            {/* CONTACT DETAILS CONTAINER SECTION */}
            <h5 className="fw-bold mb-3 d-flex align-items-center" style={{ color: THEME_COLOR }}>
              <HiOutlinePhone className="me-2" /> Contact & Billing Details
            </h5>
            <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: "16px" }}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-bold text-muted">Email Address</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-muted"><BsMailbox /></span>
                    <input 
                      type="email" 
                      className="form-control border-start-0 ps-0" 
                      placeholder="name@example.com"
                      required
                      value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label small fw-bold text-muted">Mobile Number</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-muted"><HiOutlinePhone /></span>
                    <input 
                      type="text" 
                      className="form-control border-start-0 ps-0" 
                      placeholder="10-digit mobile number"
                      required
                      pattern="[0-9]{10}"
                      value={contact.phone}
                      onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="col-md-8">
                  <label className="form-label small fw-bold text-muted">Street Address</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-muted"><HiOutlineMapPin /></span>
                    <input 
                      type="text" 
                      className="form-control border-start-0 ps-0" 
                      placeholder="Flat, House no., Apartment, Street Address"
                      required
                      value={contact.address}
                      onChange={(e) => setContact({ ...contact, address: e.target.value })}
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <label className="form-label small fw-bold text-muted">City</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="City Name"
                    required
                    value={contact.city}
                    onChange={(e) => setContact({ ...contact, city: e.target.value })}
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label small fw-bold text-muted">PinCode</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="6 digits"
                    required
                    pattern="[0-9]{6}"
                    value={contact.pincode}
                    onChange={(e) => setContact({ ...contact, pincode: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* PASSENGER PROFILE ITERATION FIELD MAP CONTAINER */}
            <h5 className="fw-bold mb-3 d-flex align-items-center" style={{ color: THEME_COLOR }}>
              <HiOutlineUser className="me-2" /> Passenger Information
            </h5>

            {passengers.map((passenger, index) => (
              <div key={index} className="card border-0 shadow-sm mb-4 overflow-hidden" style={{ borderRadius: "16px" }}>
                <div className="p-3 text-white fw-bold d-flex justify-content-between align-items-center" style={{ background: THEME_COLOR }}>
                  <span>{passenger.type} #{index + 1}</span>
                  <small className="badge bg-white text-dark opacity-75">Required field credentials</small>
                </div>
                
                <div className="p-4">
                  <div className="row g-3">
                    <div className="col-md-2">
                      <label className="form-label small fw-bold text-muted">Title</label>
                      <select 
                        className="form-select" 
                        value={passenger.title}
                        onChange={(e) => handlePassengerChange(index, "title", e.target.value)}
                      >
                        {passenger.type === "Adult" ? (
                          <>
                            <option value="Mr">Mr</option>
                            <option value="Mrs">Mrs</option>
                            <option value="Ms">Ms</option>
                          </>
                        ) : (
                          <>
                            <option value="Master">Master</option>
                            <option value="Miss">Miss</option>
                          </>
                        )}
                      </select>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label small fw-bold text-muted">First Name</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        placeholder="Given Name" 
                        required
                        value={passenger.firstName}
                        onChange={(e) => handlePassengerChange(index, "firstName", e.target.value)}
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label small fw-bold text-muted">Last Name</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        placeholder="Surname" 
                        required
                        value={passenger.lastName}
                        onChange={(e) => handlePassengerChange(index, "lastName", e.target.value)}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small fw-bold text-muted">Date of Birth (DOB)</label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0 text-muted"><HiOutlineCalendar /></span>
                        <input 
                          type="date" 
                          className="form-control border-start-0 ps-0" 
                          required
                          max={getTodayDateString()}
                          value={passenger.dob}
                          onChange={(e) => handlePassengerChange(index, "dob", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="col-12 mt-3">
                      <label className="form-label small fw-bold text-muted d-block">Gender</label>
                      <div className="d-flex gap-4">
                        {["Male", "Female"].map((g) => (
                          <div key={g} className="form-check">
                            <input 
                              className="form-check-input" 
                              type="radio" 
                              name={`gender-${index}`} 
                              id={`gender-${index}-${g}`}
                              checked={passenger.gender === g}
                              onChange={() => handlePassengerChange(index, "gender", g)}
                            />
                            <label className="form-check-label text-muted small" htmlFor={`gender-${index}-${g}`}>
                              {g}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT SIDEBAR MODULE: PRICING BREAKDOWN AND GATEWAY SELECTOR */}
          <div className="col-lg-4">
            <div className="sticky-top" style={{ top: "24px" }}>
              
              {/* WALLET INTEGRATION BALANCE SELECTION CONTROLLER */}
              {userId && (
                <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: "16px" }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 rounded-3 bg-opacity-10 text-success" style={{ background: '#e1f5fe' }}>
                        <HiOutlineWallet className="fs-3 text-primary" />
                      </div>
                      <div>
                        <h6 className="mb-0 fw-bold text-dark">Use Wallet Account</h6>
                        <small className="text-muted">Available Balance: ₹{walletBalance.toLocaleString()}</small>
                      </div>
                    </div>
                    <div className="form-check form-switch fs-4 mb-0">
                      <input 
                        className="form-check-input cursor-pointer shadow-none" 
                        type="checkbox" 
                        role="switch"
                        checked={useWallet}
                        disabled={walletBalance === 0 || isProcessing}
                        onChange={(e) => setUseWallet(e.target.checked)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TICKET ITINERARY SUMMARY REVIEW */}
              <div className="card border-0 shadow-sm overflow-hidden mb-4" style={{ borderRadius: "16px" }}>
                <div className="p-4 text-white" style={{ background: THEME_COLOR }}>
                  <h5 className="mb-0 fw-bold">Review Price Breakdown</h5>
                </div>
                <div className="p-4">
                  
                  {flight?.FlightDetails?.Details?.[0] && (
                    <div className="mb-3">
                      <span className="fw-bold text-dark d-block">Itinerary Schedule</span>
                      <small className="text-muted">
                        {flight.FlightDetails.Details[0][0].Origin.AirportCode} → {flight.FlightDetails.Details[0][flight.FlightDetails.Details[0].length - 1].Destination.AirportCode}
                      </small>
                      <hr className="my-3 opacity-10" />
                    </div>
                  )}

                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">Base Booking Ticket</span>
                    <span className="fw-bold small">₹{totalFareAmount.toLocaleString()}</span>
                  </div>

                  {/* Wallet Deductions Row representation */}
                  {appliedWalletAmount > 0 && (
                    <div className="d-flex justify-content-between mb-2 text-danger">
                      <span className="small">Wallet Offset Deductions</span>
                      <span className="fw-bold small">-₹{appliedWalletAmount.toLocaleString()}</span>
                    </div>
                  )}

                  {/* Payment gateway charges conditional rendering segment row */}
                  {platformFee > 0 && (
                    <div className="d-flex justify-content-between mb-2 text-muted">
                      <span className="small">Gateway Platform Fees (2.7%)</span>
                      <span className="fw-bold small">+₹{platformFee.toLocaleString()}</span>
                    </div>
                  )}

                  <hr className="my-3 opacity-10" />
                  
                  {/* Total Net Due Payable amount layout row rendering */}
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h6 className="mb-0 fw-bold">Net Total Due</h6>
                      <small className="text-muted">All taxes & gateway adjustments</small>
                    </div>
                    <h4 className="mb-0 fw-bold" style={{ color: THEME_COLOR }}>
                      ₹{netPayable.toLocaleString()}
                    </h4>
                  </div>

                  <button 
                    type="submit" 
                    disabled={isProcessing}
                    className="btn btn-warning w-100 fw-bold py-3 text-uppercase shadow-sm border-0 d-flex align-items-center justify-content-center gap-2"
                    style={{ borderRadius: '12px', background: '#ffb700' }}
                  >
                    {isProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                        Processing Checkout...
                      </>
                    ) : (
                      <>
                        <HiOutlineCreditCard className="fs-5" /> 
                        {baseGatewayAmount === 0 ? "Pay via Wallet" : "Proceed & Pay Online"}
                      </>
                    )}
                  </button>

                </div>
              </div>

            </div>
          </div>

        </div>
      </form>

      <style>{`
        .cursor-pointer { cursor: pointer; }
        .animate-fade-in { animation: fadeIn 0.4s ease-in; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default PassengerDetails;